const translations = {
  en: {
    nav: {
      problem:  'Problem',
      solution: 'How It Works',
      platform: 'Pricing',
      roadmap:  'Roadmap',
      about:    'About',
      whyNow:   'Why Now',
      research: 'Research',
      waitlist: 'Join Waitlist',
      login:    'Log In',
    },

    hero: {
      overline:   'MVP · PNW Pilots Opening Summer 2026',
      tagline:    'AI-powered predictive intelligence rooted in nature\'s own signals.',
      subtitle:   'Canopi forecasts survival probability from 30 days to 5 years, so reforestation teams can intervene before plots fail — not after.',
      ctaPilot:   'Pilot with us',
      ctaWaitlist:'Join the Community waitlist',
    },

    problem: {
      overline:     'The Problem',
      heading:      'Reforestation has a',
      headingGreen: 'data problem.',
      body:         'Despite global investment, nearly 50% of replanted trees fail to survive past five years. Current carbon credit models often rely on broad projections that lack granular data on local climate, soil, and terrain. While decades of forest inventory and high-resolution environmental data exist, they remain fragmented. Without a unified tool, even the most dedicated restoration teams are left to navigate complex ecosystems without a clear data-driven roadmap.',
      note:         'Canopi connects it.',
      cards: [
        { title: 'Infrequent check-ins', desc: 'Check-ins months apart, separated by entire seasons or limited due to high costs.' },
        { title: 'Late detection',       desc: 'Decline goes undetected, intervention comes too late, losses are already locked in.' },
        { title: 'Revenue at risk',      desc: 'Carbon credits go unissued, project developers absorb losses, credit buyers lose confidence.' },
      ],
    },

    solution: {
      overline:    'How It Works',
      heading:     'How Canopi',
      headingGreen:'works',
      subtitle:    'Three steps from site selection to actionable intelligence — built on 30 years of environmental data.',
      steps: [
        {
          num:   '01',
          label: 'Select your site',
          desc:  'Whether you are scouting new locations or onboarding existing plots, simply choose a location, confirm species, and planting methods. Canopi pulls 30 years of climate, soil, and terrain data matched to your specific plot.',
        },
        {
          num:   '02',
          label: 'Generate Predictive Forecasts',
          desc:  'Get an instant survival probability from 30 days to 5 years. Our models analyze the specific environmental risk factors—like micro-climate shifts and soil composition—that drive outcomes, allowing you to identify vulnerabilities before they become losses.',
        },
        {
          num:   '03',
          label: 'Sync, Predict, and Scale',
          desc:  'Update Canopi with results from your latest field check-ins to see real-time forecast adjustments. As survival data evolves, Canopi generates carbon metric estimates, turning field observations into informed climate impact.',
        },
      ],
    },

    whySmarter: {
      overline:    'Why Canopi Gets Smarter Over Time',
      heading:     'A model that grows',
      headingGreen:'with the forest.',
      body:        'The climate isn\'t static, and neither are the species that thrive in it. Restoration practitioners are already adapting — planting different species today than they would have a decade ago, because conditions on the ground have shifted.\n\nCanopi is built for this reality. Every project that runs on Canopi contributes signal back to the model — survival outcomes, planting methods, site conditions, intervention results. The more practitioners use it, the sharper the predictions become for everyone.\n\nThis is how Canopi stays accurate as the climate keeps moving. Not a static model trained once and frozen, but a living system that learns from every forest it touches.',
      pillars: [
        { label: 'Always learning.',   desc: 'Each pilot, each plot, each season improves the model.' },
        { label: 'Climate-adaptive.',  desc: 'Predictions evolve as the conditions trees actually face evolve.' },
        { label: 'Network effect.',    desc: 'The forest speaks in layers. Soil chemistry, climate history, canopy gaps, root competition — no single signal tells the whole story. Canopi listens to all of it.' },
      ],
    },

    audience: {
      overline:     'Who It\'s For',
      heading:      'Two audiences.',
      headingGreen: 'One engine.',
      cards: [
        {
          title: 'For Carbon Project Developers',
          desc:  'You need survival confidence before you deploy capital, not after. Canopi gives you plot-level risk forecasts, carbon projections, and portfolio analytics — so your credits hold and your investors trust the data behind them.',
          cta:   'Request pilot access',
          community: false,
        },
        {
          title: 'For Nonprofits & Land Stewards',
          desc:  'You steward forests with limited resources and zero predictive tools. Canopi\'s Community tier gives you the same survival intelligence — free, pre-loaded with open data, built for teams where every tree and every dollar matters.',
          cta:   'Join the Community waitlist',
          community: true,
        },
      ],
    },

    platform: {
      overline:     'Platform',
      heading:      'Two tiers,',
      headingGreen: 'built for everyone.',
      subtitle:     'Community access is free — always. The Commercial tier unlocks the full intelligence stack for project developers, portfolio managers, and reforestation operators.',
      community: {
        name:    'Community',
        badge:   'Free',
        tagline: 'For nonprofits, indigenous forestry programs, and conservation organizations.',
        cta:     'Join the Community waitlist',
        forever: 'Free forever',
        features: [
          'Survival risk dashboard',
          'Open-source environmental data',
          'Project and plot management',
          'Up to 3 users',
        ],
      },
      commercial: {
        name:    'Commercial',
        badge:   'Commercial',
        tagline: 'For carbon project developers, VCM portfolio managers, and reforestation operators.',
        divider: 'Everything in Community, plus',
        cta:     'Request pilot access',
        note:    'Pricing available upon request. Enterprise needs? Let\'s talk.',
        features: [
          { label: 'Upload your own planting data and field observations', highlight: true  },
          { label: 'Carbon projections with registry-formatted reports',   highlight: false },
          { label: 'API access for platform integration',                  highlight: false },
          { label: 'Up to 10 users',                                       highlight: false },
          { label: 'Scenario modeling across species, method, and climate pathway', highlight: false, note: 'Coming soon' },
          { label: 'Portfolio analytics and regional benchmarking',        highlight: false, note: 'Coming soon' },
          { label: 'Survival alerts and climate anomaly monitoring',       highlight: false, note: 'Coming soon' },
        ],
      },
    },

    whyNow: {
      overline:     'Why Now',
      heading:      'The industry is catching up to',
      headingGreen: 'what the science already knows.',
      body:         'Carbon registries are tightening permanence standards. The IC-VCM framework, COP29\'s Article 6.4 guidance, and updated methodologies from Verra and Gold Standard all point the same direction: place-based, data-driven risk assessment — not uniform buffer pools.',
      stat:         '596,000',
      statLabel:    'tree-remeasurement pairs',
      statBody:     'Canopi\'s model is built on this principle. Temporally matched climate data. Plot-specific soil and terrain. Three decades of USDA Forest Inventory measurements. This isn\'t a future standard. It\'s already here.',
    },

    roadmap: {
      overline:     'Roadmap',
      heading:      'Built in public.',
      headingGreen: 'Shipping in phases.',
      subtitle:     'Each Canopi model release follows the Mycelium Series — named after the life stages of fungi, the underground networks that quietly hold forests together.',
      phases: [
        { stage: 'Spore (v0.1)',  phase: 'Phase 1', title: 'Now',         date: 'Now',         desc: 'MVP survival model · PNW dashboard · Community tier · Pilot onboarding', active: true  },
        { stage: 'Mycel (v0.2)',  phase: 'Phase 2', title: 'Summer 2026', date: 'Summer 2026', desc: 'Pilot data integration · Carbon projections · Commercial tier · API v1',  active: false },
        { stage: 'Hypha (v1.0)',  phase: 'Phase 3', title: 'Fall 2026',   date: 'Fall 2026',   desc: 'Scenario modeling · Intervention recommendations · Expanded US regions',   active: false },
        { stage: 'Rhizo (v1.5)',  phase: 'Phase 4', title: '2027',        date: '2027',        desc: 'Planting site optimization · Species recommendations · Canada + LATAM',    active: false },
      ],
    },

    founder: {
      overline: 'About the Founder',
      name:     'Gabby Larios',
      role:     'Founder',
      bio1:     'Gabby is a product leader with a deep technical foundation, having spent nearly a decade building predictive intelligence systems for some of the hardest operating environments — autonomous maritime platforms processing real-time sensor data, edge AI navigation where GPS doesn\'t exist, and computer vision tools deployed at the edge of connectivity.',
      bio2:     'Reforestation has been a parallel passion for years. Canopi is where the two converge — a commitment to ensuring the world\'s most critical reforestation efforts have access to the same caliber of intelligence once reserved for the high-tech frontier.',
      quote:    '"This isn\'t about the technology; it\'s about reconnecting to our role as stewards of the earth. We\'ve spent decades perfecting precision for our machines and industries, giving our forests that same level of excellence is the absolute least we can do"',
      linkedin: 'Connect on LinkedIn',
    },

    research: {
      overline:   'Research',
      heading:    'The science behind the',
      headingGreen: 'predictions',
      subtitle:   'Canopi\'s predictive models are grounded in peer-reviewed research and decades of environmental data. This section will be populated with the publications and datasets that inform our approach.',
      pending:    'Publication pending',
      disclaimer: 'Research publications and datasets will be added here as they become available. Canopi is committed to transparency in the science that drives our predictive engine.',
      topics: [
        { title: 'Species-Specific Survival Dynamics',  desc: 'How seedling survival curves vary by species, planting method, and site conditions across the Pacific Northwest.' },
        { title: 'Remote Sensing + ML Fusion',          desc: 'Integrating satellite imagery with ground-truth data to calibrate predictive models without continuous field visits.' },
        { title: 'Soil–Climate Interaction Models',     desc: 'Modeling how soil moisture, temperature, and drought stress compound to predict plot-level outcomes.' },
        { title: 'Carbon Credit Risk Quantification',   desc: 'Translating survival probability distributions into credit issuance risk and financial exposure metrics.' },
      ],
    },

    interest: {
      closingOverline:     'Get Started',
      closingHeading:      'Give every seedling the intelligence',
      closingHeadingGreen: 'to survive.',
      closingBody:         'Reforestation is too important to run on guesswork. Canopi gives you the intelligence to plant with confidence — and a model that keeps learning alongside the forests it serves.',
      ctaPilot:            'Pilot with us',
      ctaWaitlist:         'Join the Community waitlist',

      pilotOverline:    'Pilot Interest',
      pilotHeading:     'Ready to pilot with',
      pilotHeadingGreen:'Canopi?',
      pilotBody:        "PNW pilots open Summer 2026. If you're a project developer or reforestation operator in Oregon or Washington, we'd love to connect. No commitment required.",
      namePlaceholder:  'Full Name',
      emailPlaceholder: 'Email Address',
      orgPlaceholder:   'Organization (optional)',
      submitPilot:      'Request Pilot Access',
      disclaimer:       'No commitment required. We\'ll reach out when pilots open.',

      communityOverline:    'Community Tier',
      communityHeading:     'Join the Community',
      communityHeadingGreen:'waitlist',
      communityBody:        'Free survival intelligence for nonprofits, indigenous forestry programs, and conservation organizations. Be first to access the dashboard when it launches.',
      communitySubmit:      'Join the Community waitlist',
      communityDisclaimer:  'No spam — just updates on our progress.',
    },

    footer: {
      tagline:    'Predictive intelligence for reforestation. AI-powered survival risk forecasting from 30 days to 5 years.',
      slogan:     'Built for the forests that can\'t wait.',
      product:    'Product',
      company:    'Company',
      getStarted: 'Get Started',
      links: {
        solution:  'How It Works',
        platform:  'Pricing',
        roadmap:   'Roadmap',
        about:     'About',
        research:  'Research',
        contact:   'Contact',
        pilot:     'Pilot with us',
        waitlist:  'Join Waitlist',
        login:     'Log In',
      },
    },
  },

  /* ══════════════════════════════════════════════════════════
     ESPAÑOL
     ══════════════════════════════════════════════════════════ */
  es: {
    nav: {
      problem:  'Problema',
      solution: 'Cómo Funciona',
      platform: 'Precios',
      roadmap:  'Hoja de Ruta',
      about:    'Nosotros',
      whyNow:   'Por Qué Ahora',
      research: 'Investigación',
      waitlist: 'Únete a la Lista',
      login:    'Iniciar Sesión',
    },

    hero: {
      overline:   'MVP · Pilotos en el PNW a partir del Verano 2026',
      tagline:    'Inteligencia predictiva con IA, arraigada en las señales de la propia naturaleza.',
      subtitle:   'Canopi pronostica la probabilidad de supervivencia de 30 días a 5 años, para que los equipos de reforestación puedan intervenir antes de que los proyectos fallen — no después.',
      ctaPilot:   'Participa como Piloto',
      ctaWaitlist:'Únete a la lista comunitaria',
    },

    problem: {
      overline:     'El Problema',
      heading:      'La reforestación tiene un',
      headingGreen: 'problema de datos.',
      body:         'A pesar de la inversión global, casi el 50% de los árboles replantados no sobrevive más de cinco años. Los modelos actuales de créditos de carbono suelen basarse en proyecciones generales que carecen de datos granulares sobre el clima local, el suelo y el terreno. Aunque existen décadas de inventarios forestales y datos ambientales de alta resolución, estos permanecen fragmentados. Sin una herramienta unificada, incluso los equipos de restauración más dedicados deben navegar ecosistemas complejos sin una hoja de ruta clara impulsada por datos.',
      note:         'Canopi los conecta.',
      cards: [
        { title: 'Visitas esporádicas',  desc: 'Revisiones con meses de diferencia, separadas por estaciones enteras o limitadas por costos elevados.' },
        { title: 'Detección tardía',     desc: 'El deterioro pasa desapercibido, la intervención llega demasiado tarde y las pérdidas ya están consolidadas.' },
        { title: 'Ingresos en riesgo',   desc: 'Los créditos de carbono no se emiten, los desarrolladores absorben pérdidas y los compradores pierden confianza.' },
      ],
    },

    solution: {
      overline:    'Cómo Funciona',
      heading:     'Cómo funciona',
      headingGreen:'Canopi',
      subtitle:    'Tres pasos desde la selección del sitio hasta la inteligencia accionable — basados en 30 años de datos ambientales.',
      steps: [
        {
          num:   '01',
          label: 'Seleccione su sitio',
          desc:  'Ya sea que esté explorando nuevas ubicaciones o integrando parcelas existentes, simplemente elija la ubicación y confirme las especies y los métodos de siembra. Canopi extrae 30 años de datos climáticos, de suelo y de terreno vinculados específicamente a su parcela.',
        },
        {
          num:   '02',
          label: 'Genere pronósticos predictivos',
          desc:  'Obtenga una probabilidad de supervivencia instantánea desde 30 días hasta 5 años. Nuestros modelos analizan los factores de riesgo ambientales específicos —como variaciones en el microclima y composición del suelo— que determinan los resultados, permitiéndole identificar vulnerabilidades antes de que se conviertan en pérdidas.',
        },
        {
          num:   '03',
          label: 'Sincronice, prediga y escale',
          desc:  'Actualice Canopi con los resultados de sus inspecciones de campo más recientes para visualizar ajustes en los pronósticos en tiempo real. A medida que los datos de supervivencia evolucionan, Canopi genera estimaciones de métricas de carbono, transformando las observaciones de campo en un impacto climático informado.',
        },
      ],
    },

    whySmarter: {
      overline:    'Por Qué Canopi Mejora con el Tiempo',
      heading:     'Un modelo que crece',
      headingGreen:'con el bosque.',
      body:        'El clima no es estático, y tampoco lo son las especies que prosperan en él. Los practicantes de restauración ya se están adaptando — plantando especies diferentes hoy que hace una década, porque las condiciones en el campo han cambiado.\n\nCanopi está construido para esta realidad. Cada proyecto que usa Canopi contribuye señales al modelo — resultados de supervivencia, métodos de plantación, condiciones del sitio, resultados de intervención. Cuantos más practicantes lo usen, más precisas serán las predicciones para todos.\n\nAsí es como Canopi mantiene su precisión mientras el clima sigue cambiando. No un modelo estático entrenado una vez y congelado, sino un sistema vivo que aprende de cada bosque que toca.',
      pillars: [
        { label: 'Siempre aprendiendo.',   desc: 'Cada piloto, cada parcela, cada temporada mejora el modelo.' },
        { label: 'Adaptado al clima.',     desc: 'Las predicciones evolucionan a medida que evolucionan las condiciones que enfrentan los árboles.' },
        { label: 'Efecto de red.',         desc: 'El bosque tiene múltiples capas. La química de la tierra, el pasado del clima, los espacios en el dosel y la lucha de las raíces... ninguna señal basta por sí sola. Canopi sabe escucharlas todas.' },
      ],
    },

    audience: {
      overline:     'Para Quién Es',
      heading:      'Dos audiencias.',
      headingGreen: 'Un motor.',
      cards: [
        {
          title: 'Para Desarrolladores de Proyectos de Carbono',
          desc:  'Necesitas confianza en la supervivencia antes de desplegar capital, no después. Canopi te da pronósticos de riesgo por parcela, proyecciones de carbono y análisis de portafolio — para que tus créditos se mantengan y tus inversores confíen en los datos.',
          cta:   'Solicitar acceso piloto',
          community: false,
        },
        {
          title: 'Para ONGs y Custodios de la Tierra',
          desc:  'Custodias bosques con recursos limitados y cero herramientas predictivas. El nivel Comunitario de Canopi te da la misma inteligencia de supervivencia — gratis, precargado con datos abiertos, construido para equipos donde cada árbol y cada dólar importa.',
          cta:   'Únete a la lista comunitaria',
          community: true,
        },
      ],
    },

    platform: {
      overline:     'Plataforma',
      heading:      'Dos niveles,',
      headingGreen: 'para todos.',
      subtitle:     'El acceso comunitario es gratuito — siempre. El nivel comercial desbloquea la inteligencia completa para desarrolladores de proyectos, gestores de portafolio y operadores de reforestación.',
      community: {
        name:    'Comunitario',
        badge:   'Gratis',
        tagline: 'Para organizaciones sin fines de lucro, programas forestales indígenas y organizaciones de conservación.',
        cta:     'Únete a la lista comunitaria',
        forever: 'Gratis para siempre',
        features: [
          'Panel de riesgo de supervivencia',
          'Datos ambientales de código abierto',
          'Gestión de proyectos y parcelas',
          'Hasta 3 usuarios',
        ],
      },
      commercial: {
        name:    'Comercial',
        badge:   'Comercial',
        tagline: 'Para desarrolladores de proyectos de carbono, gestores de portafolio VCM y operadores de reforestación.',
        divider: 'Todo lo del nivel Comunitario, más',
        cta:     'Solicitar acceso piloto',
        note:    'Precios anuales personalizados disponibles para portafolios más grandes. ¿Necesidades empresariales? Hablemos.',
        features: [
          { label: 'Sube tus propios datos de plantación y observaciones de campo', highlight: true  },
          { label: 'Proyecciones de carbono con informes formateados para registros', highlight: false },
          { label: 'Acceso a API para integración de plataforma',                     highlight: false },
          { label: 'Hasta 10 usuarios',                                                highlight: false },
          { label: 'Modelado de escenarios por especie, método y escenario climático', highlight: false, note: 'Próximamente' },
          { label: 'Análisis de portafolio y benchmarking regional',                  highlight: false, note: 'Próximamente' },
          { label: 'Alertas de supervivencia y monitoreo de anomalías climáticas',    highlight: false, note: 'Próximamente' },
        ],
      },
    },

    whyNow: {
      overline:     'Por Qué Ahora',
      heading:      'La industria está alcanzando',
      headingGreen: 'lo que la ciencia ya sabe.',
      body:         'Los registros de carbono están endureciendo los estándares de permanencia. El marco IC-VCM, la guía del Artículo 6.4 de la COP29, y las metodologías actualizadas de Verra y Gold Standard apuntan en la misma dirección: evaluación de riesgo basada en lugar y datos — no grupos de amortiguamiento uniformes.',
      stat:         '596,000',
      statLabel:    'pares de remediciones de árboles',
      statBody:     'El modelo de Canopi está construido sobre este principio. Datos climáticos con coincidencia temporal. Suelo y terreno específicos del sitio. Tres décadas de mediciones del Inventario Forestal de la USDA. Este no es un estándar futuro. Ya está aquí.',
    },

    roadmap: {
      overline:     'Hoja de Ruta',
      heading:      'Construido en público.',
      headingGreen: 'Lanzando en fases.',
      subtitle:     'Cada versión del modelo de Canopi sigue la Serie Micelio — nombrada según las etapas de vida de los hongos, las redes subterráneas que sostienen silenciosamente los bosques.',
      phases: [
        { stage: 'Espora (v0.1)',  phase: 'Fase 1', title: 'Ahora',        date: 'Ahora',        desc: 'Modelo de supervivencia MVP · Panel del PNW · Nivel Comunitario · Incorporación de pilotos', active: true  },
        { stage: 'Micel (v0.2)',   phase: 'Fase 2', title: 'Verano 2026',  date: 'Verano 2026',  desc: 'Integración de datos piloto · Proyecciones de carbono · Nivel Comercial · API v1',           active: false },
        { stage: 'Hifa (v1.0)',    phase: 'Fase 3', title: 'Otoño 2026',   date: 'Otoño 2026',   desc: 'Modelado de escenarios · Recomendaciones de intervención · Regiones ampliadas de EE.UU.',    active: false },
        { stage: 'Rizo (v1.5)',    phase: 'Fase 4', title: '2027',         date: '2027',         desc: 'Optimización de sitios de plantación · Recomendaciones de especies · Canadá + LATAM',        active: false },
      ],
    },

    founder: {
      overline: 'Sobre la Fundadora',
      name:     'Gabby Larios',
      role:     'Fundadora',
      bio1:     'Gabby es una líder de producto con una sólida base técnica, con casi una década construyendo sistemas de inteligencia predictiva para algunos de los entornos operativos más exigentes — plataformas marítimas autónomas que procesan datos de sensores en tiempo real, navegación de IA en el borde donde el GPS no existe, y herramientas de visión por computadora desplegadas en los límites de la conectividad.',
      bio2:     'La reforestación ha sido una pasión paralela durante años. Canopi es donde ambas convergen — un compromiso para garantizar que los esfuerzos de reforestación más críticos del mundo tengan acceso a la misma inteligencia que antes estaba reservada para la frontera de alta tecnología.',
      quote:    '"Esto no se trata de la tecnología; se trata de reconectarnos con nuestro papel como guardianes de la tierra. Hemos pasado décadas perfeccionando la precisión de nuestras máquinas e industrias; brindar ese mismo nivel de excelencia a nuestros bosques es lo mínimo que podemos hacer."',
      linkedin: 'Conectar en LinkedIn',
    },

    research: {
      overline:     'Investigación',
      heading:      'La ciencia detrás de las',
      headingGreen: 'predicciones',
      subtitle:     'Los modelos predictivos de Canopi están fundamentados en investigación revisada por pares y décadas de datos ambientales. Esta sección se irá completando con las publicaciones y conjuntos de datos que informan nuestro enfoque.',
      pending:      'Publicación pendiente',
      disclaimer:   'Las publicaciones e investigaciones se añadirán aquí a medida que estén disponibles. Canopi está comprometida con la transparencia en la ciencia que impulsa nuestro motor predictivo.',
      topics: [
        { title: 'Dinámicas de Supervivencia por Especie',           desc: 'Cómo varían las curvas de supervivencia de plántulas según especie, método de plantación y condiciones del sitio en el noroeste del Pacífico.' },
        { title: 'Fusión de Teledetección + ML',                     desc: 'Integrar imágenes satelitales con datos de campo para calibrar modelos predictivos sin visitas continuas.' },
        { title: 'Modelos de Interacción Suelo–Clima',               desc: 'Modelar cómo la humedad del suelo, la temperatura y el estrés hídrico se combinan para predecir resultados a nivel de parcela.' },
        { title: 'Cuantificación de Riesgo de Créditos de Carbono',  desc: 'Traducir distribuciones de probabilidad de supervivencia en riesgo de emisión de créditos y métricas de exposición financiera.' },
      ],
    },

    interest: {
      closingOverline:     'Comenzar',
      closingHeading:      'Dale a cada plántula la inteligencia',
      closingHeadingGreen: 'para sobrevivir.',
      closingBody:         'La reforestación es demasiado importante para basarse en conjeturas. Canopi te da la inteligencia para plantar con confianza — y un modelo que sigue aprendiendo junto a los bosques que sirve.',
      ctaPilot:            'Participa como Piloto',
      ctaWaitlist:         'Únete a la lista comunitaria',

      pilotOverline:    'Interés en Piloto',
      pilotHeading:     '¿Listo para pilotear con',
      pilotHeadingGreen:'Canopi?',
      pilotBody:        'Los pilotos del PNW abren en el Verano 2026. Si eres desarrollador de proyectos u operador de reforestación en Oregón o Washington, nos encantaría conectar. Sin compromiso.',
      namePlaceholder:  'Nombre completo',
      emailPlaceholder: 'Correo electrónico',
      orgPlaceholder:   'Organización (opcional)',
      submitPilot:      'Solicitar Acceso Piloto',
      disclaimer:       'Sin compromiso. Te contactaremos cuando se abran los pilotos.',

      communityOverline:    'Nivel Comunitario',
      communityHeading:     'Únete a la lista',
      communityHeadingGreen:'comunitaria',
      communityBody:        'Inteligencia de supervivencia gratuita para organizaciones sin fines de lucro, programas forestales indígenas y organizaciones de conservación. Sé el primero en acceder al panel cuando se lance.',
      communitySubmit:      'Únete a la lista comunitaria',
      communityDisclaimer:  'Sin spam — solo actualizaciones sobre nuestro progreso.',
    },

    footer: {
      tagline:    'Inteligencia predictiva para la reforestación. Pronóstico de riesgo de supervivencia con IA de 30 días a 5 años.',
      slogan:     'Hecho para los bosques que no pueden esperar.',
      product:    'Producto',
      company:    'Empresa',
      getStarted: 'Comenzar',
      links: {
        solution:  'Cómo Funciona',
        platform:  'Precios',
        roadmap:   'Hoja de Ruta',
        about:     'Nosotros',
        research:  'Investigación',
        contact:   'Contacto',
        pilot:     'Participa como Piloto',
        waitlist:  'Únete a la Lista',
        login:     'Iniciar Sesión',
      },
    },
  },
};

export default translations;
